import { useEffect, useState } from "react";
import { Check, X, Loader2 } from "lucide-react";
import { useMarket } from "../../lib/store";
import { hist } from "../../lib/hist";
import { useMotion } from "./MotionProvider";

// 03 sparkline — last ~25 LTPs kept in memory per symbol
export function Sparkline({ k, w = 54, h = 18 }) {
  const ltp = useMarket((s) => s.ticks[k]?.ltp);
  const arr = hist.get(k) || [];
  if (arr.length < 2) return <svg width={w} height={h} aria-hidden="true" />;
  const lo = Math.min(...arr);
  const hi = Math.max(...arr);
  const span = hi - lo || 1;
  const d = arr.map((v, i) => `${(i / (arr.length - 1)) * w},${h - 2 - ((v - lo) / span) * (h - 4)}`).join(" ");
  const up = ltp >= arr[0];
  return (
    <svg width={w} height={h} aria-hidden="true" data-testid={`sparkline-${k}`}>
      <polyline points={d} fill="none" stroke={up ? "hsl(var(--up))" : "hsl(var(--down))"} strokeWidth="1.25" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// 07 order button — idle → spinner → tick "Filled at ₹X" / shake + red on rejection
export function OrderButton({ state, label, side, onClick, testid, filledText }) {
  const { mode } = useMotion();
  const bg = state === "rejected" ? "bg-down" : state === "filled" ? "bg-up" : side === "BUY" ? "bg-up" : "bg-down";
  return (
    <button data-testid={testid} data-state={state} disabled={state === "loading" || state === "filled"} onClick={onClick}
      className={`relative h-10 min-w-[140px] px-5 rounded-md text-white font-semibold text-[13px] tracking-wide flex items-center justify-center gap-2 hover:brightness-110 active:scale-[0.98] transition-[filter,transform,background-color] duration-200 disabled:cursor-default ${bg} ${state === "rejected" && mode !== "off" ? "m-shake" : ""}`}>
      {state === "loading" && <Loader2 className="h-4 w-4 animate-spin" />}
      {state === "filled" && <span className="m-pop grid place-items-center h-4 w-4 rounded-full bg-white/25"><Check className="h-3 w-3" /></span>}
      {state === "rejected" && <X className="h-4 w-4" />}
      <span data-testid={`${testid}-label`}>{state === "filled" ? filledText : state === "rejected" ? "Rejected" : state === "loading" ? "Placing…" : label}</span>
    </button>
  );
}

// 08 buy/sell switch — sliding pill
export function SideSwitch({ side, onChange }) {
  return (
    <div className="relative grid grid-cols-2 w-36 h-7 rounded-full bg-black/25 p-0.5 text-[10.5px] font-bold tracking-wider" data-testid="order-side-switch" role="radiogroup">
      <span className="absolute top-0.5 bottom-0.5 w-[calc(50%-2px)] rounded-full bg-white shadow transition-transform duration-300"
        style={{ transform: side === "SELL" ? "translateX(100%)" : "translateX(0)", left: 2, transitionTimingFunction: "var(--m-overshoot)" }} />
      {["BUY", "SELL"].map((s) => (
        <button key={s} role="radio" aria-checked={side === s} data-testid={`order-side-${s}`} onClick={() => side !== s && onChange(s)}
          className={`relative z-10 transition-colors duration-200 ${side === s ? (s === "BUY" ? "text-up" : "text-down") : "text-white/80"}`}>{s}</button>))}
    </div>
  );
}

// 21 skeleton rows for tables
export function SkeletonRows({ rows = 6, cols = 6, testid = "table-skeleton" }) {
  return (
    <div className="p-3 space-y-2" data-testid={testid} aria-busy="true">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="grid gap-3" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
          {Array.from({ length: cols }).map((__, c) => <span key={c} className="m-skel h-3.5 rounded" style={{ width: `${55 + ((r * 7 + c * 13) % 40)}%`, animationDelay: `${(r * cols + c) * 40}ms` }} />)}
        </div>))}
    </div>
  );
}

// 30 gooey connect overlay while the market socket (re)connects
export function GooeyConnect({ className = "" }) {
  const ws = useMarket((s) => s.wsState);
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (ws === "open") { setShow(false); return undefined; }
    const t = setTimeout(() => setShow(true), 900);
    return () => clearTimeout(t);
  }, [ws]);
  if (!show) return null;
  return (
    <div className={`absolute inset-0 z-30 grid place-items-center bg-background/70 backdrop-blur-sm m-fade ${className}`} data-testid="feed-reconnecting-overlay" role="status">
      <div className="flex flex-col items-center gap-3">
        <svg width="0" height="0" className="absolute"><filter id="goo"><feGaussianBlur in="SourceGraphic" stdDeviation="5" /><feColorMatrix values="1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 20 -9" /></filter></svg>
        <div className="relative h-8 w-24" style={{ filter: "url(#goo)" }}>
          {[0, 1, 2].map((i) => <span key={i} className="m-goo absolute top-1/2 h-4 w-4 -mt-2 rounded-full bg-primary" style={{ left: 8 + i * 28, animationDelay: `${i * 160}ms` }} />)}
        </div>
        <span className="text-[12px] text-muted-foreground">Reconnecting to market feed…</span>
      </div>
    </div>
  );
}

// 26 orbit loader (full-page)
export function OrbitLoader({ label = "Loading terminal…" }) {
  return (
    <div className="h-full min-h-[50vh] grid place-items-center" data-testid="orbit-loader" role="status">
      <div className="flex flex-col items-center gap-4">
        <div className="relative h-14 w-14 motion-decor">
          <span className="absolute inset-0 rounded-full border border-primary/25" />
          <span className="absolute inset-0 m-orbit"><span className="absolute -top-1 left-1/2 -ml-1 h-2 w-2 rounded-full bg-primary shadow-[0_0_12px_hsl(var(--gold))]" /></span>
          <span className="absolute inset-3 m-orbit-rev"><span className="absolute -top-0.5 left-1/2 -ml-[3px] h-1.5 w-1.5 rounded-full bg-[hsl(var(--gold-2))]" /></span>
          <span className="absolute inset-[22px] rounded-full bg-gold" />
        </div>
        <span className="text-[12px] text-muted-foreground">{label}</span>
      </div>
    </div>
  );
}

// 04 candles build — chart loading placeholder
export function CandlesBuild() {
  const H = [38, 52, 44, 61, 55, 70, 63, 48, 57, 72, 66, 80, 69, 58, 74, 86, 77, 64];
  return (
    <div className="absolute inset-0 flex items-end justify-center gap-[6px] pb-10 px-10" data-testid="chart-candles-build" aria-label="Loading chart">
      {H.map((h, i) => (
        <span key={i} className="m-candle w-[10px] rounded-[2px] origin-bottom" style={{ height: `${h * 0.6}%`, animationDelay: `${i * 55}ms`, background: i % 3 === 1 ? "hsl(var(--down) / 0.55)" : "hsl(var(--up) / 0.55)" }} />))}
    </div>
  );
}
